'use client';

import React, { useCallback, useState, useRef, useEffect } from 'react';
import Icon from '@/components/shared/Icons';
import dynamic from 'next/dynamic';
import { getSimpleClickFact } from '@/analytics/helpers/cventAnalytics/getSimpleClickFact';
import { convertToSnakeCase, VENUE_CARD_CLICK_TYPE } from '@/utils/analyticsClientHelper';
import useMap from '@/hooks/useMap';
import useAnalytics from '@/hooks/useAnalytics';
import type { SimpleClickFact } from '@/analytics/models/cventAnalytics/SimpleClickFact';
import { Tag as TagType } from '@/constants/tagConstants';

const Popper = dynamic(() => import('@/components/shared/popper/Popper'), { ssr: false });

interface PopperContentProps {
  showTitle: boolean;
  name: string;
  content: JSX.Element | string;
  isOpen: boolean;
  triggerRef: React.RefObject<HTMLDivElement | HTMLButtonElement>;
}

export interface GenericTagProps {
  tag: TagType;
  searchResultsRank: number;
  searchTerm: string;
  venueId: string;
  venueDiamondType?: string;
  includedTagsForLoggingFacts?: Record<string, string>;
}

function PopperContent({ showTitle, name, content, isOpen, triggerRef }: PopperContentProps): JSX.Element {
  return (
    <Popper triggerRef={triggerRef} isOpen={isOpen} arrowSize="small" placement="top" preventOverflow>
      <div className="w-auto max-w-xs overflow-y-hidden rounded-lg bg-background p-3 text-b-xs/tight text-neutral-70 shadow-4">
        {showTitle && <h3 className="mb-2 text-b-base/tight font-medium uppercase">{name}</h3>}
        {content}
      </div>
    </Popper>
  );
}

function Tag({
  tag,
  venueDiamondType,
  venueId,
  searchResultsRank,
  searchTerm,
  includedTagsForLoggingFacts
}: GenericTagProps): JSX.Element {
  const style = { backgroundColor: `#${tag.color}` };
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef(null);

  const { mapExpanded } = useMap();
  const analytics = useAnalytics();

  const triggerSimpleClickFact = useCallback(() => {
    const tagName = `venue_card_${convertToSnakeCase(includedTagsForLoggingFacts?.[tag.name])}`;
    const simpleClickFact = getSimpleClickFact(
      tagName,
      VENUE_CARD_CLICK_TYPE,
      searchResultsRank,
      venueId,
      mapExpanded,
      searchTerm,
      venueDiamondType
    );
    analytics.reportFact<SimpleClickFact>(simpleClickFact);
  }, [
    analytics,
    mapExpanded,
    venueId,
    searchResultsRank,
    searchTerm,
    tag.name,
    venueDiamondType,
    includedTagsForLoggingFacts
  ]);

  const onDismiss = useCallback(() => {
    setIsOpen(false);
  }, []);

  const onMouseEvent = useCallback(
    async e => {
      e.preventDefault();
      if (!isOpen && includedTagsForLoggingFacts?.[tag.name]) {
        triggerSimpleClickFact();
      }
      setIsOpen(!isOpen);
    },
    [isOpen, includedTagsForLoggingFacts, tag.name, triggerSimpleClickFact]
  );
  const onClick = useCallback(
    async e => {
      if (e.pointerType === 'touch') {
        if (!isOpen) {
          triggerSimpleClickFact();
        }
        setIsOpen(!isOpen);
      }
      e.stopPropagation();
      e.preventDefault();
    },
    [isOpen, triggerSimpleClickFact]
  );

  useEffect(() => {
    if (isOpen) {
      window.addEventListener('scroll', onDismiss, false);
    }
    return () => {
      window.removeEventListener('scroll', onDismiss, false);
    };
  }, [isOpen, onDismiss]);

  const tagText = (
    <span className="group">
      {tag.name}
      {tag.textIcon && (
        <Icon
          id={tag.textIcon}
          className={`${
            tag.textIconPlacement === 'left'
              ? `float-left pr-1 ${tag.useHBBranding ? 'fill-hb-interactive-90' : ''}`
              : 'float-right pl-1'
          } h-4 w-4`}
        />
      )}
    </span>
  );

  const fullTag = tag.isInteractive ? (
    <>
      <button
        type="button"
        className="text-interactive-900 h-fit w-fit items-center overflow-hidden rounded-full bg-neutral-20 px-2 py-0.5 text-b-xs/snug font-normal"
        ref={triggerRef}
        style={style}
        onClick={onClick}
        onMouseEnter={onMouseEvent}
        onMouseLeave={onDismiss}
      >
        {tagText}
      </button>
    </>
  ) : (
    <>
      <div
        className={`h-fit w-fit items-center overflow-hidden rounded-full ${
          tag.useHBBranding ? 'bg-hb-neutral-20 text-hb-interactive-90' : 'bg-neutral-20 text-interactive-90'
        } px-2 py-0.5 text-b-xs/snug font-normal`}
        ref={triggerRef}
        style={style}
        onMouseEnter={onMouseEvent}
        onMouseLeave={onDismiss}
      >
        {tagText}
      </div>
    </>
  );

  return (
    <>
      {tag.icon ? (
        // eslint-disable-next-line jsx-a11y/control-has-associated-label
        <button type="button" ref={triggerRef} onClick={onClick} onMouseEnter={onMouseEvent} onMouseLeave={onDismiss}>
          <span>
            <Icon id={tag.icon} className="h-4 w-4 fill-neutral-60" title={tag.name} />
          </span>
        </button>
      ) : (
        fullTag
      )}

      {tag.content && isOpen && (
        <PopperContent
          showTitle={tag.showTitle ?? false}
          name={tag.name}
          content={tag.content}
          isOpen={isOpen}
          triggerRef={triggerRef}
        />
      )}
      {tag.showTooltip && isOpen && (
        <Popper
          triggerRef={triggerRef}
          isOpen={isOpen}
          placement="top"
          arrowSize="small"
          arrowStyles={{
            borderColor: '#4e545b'
          }}
        >
          <div className="h-max w-max overflow-hidden rounded-sm bg-neutral-70 p-2 text-b-xs/tight text-background">
            {tag.name}
          </div>
        </Popper>
      )}
    </>
  );
}

export default Tag;
